// Particle textures (research §2 "Particles and juice"): spark streak, soft dot (radial gradient), 4-point
// star, mini coin, smoke puff, shockwave ring, confetti bits (the three confetti colours) and (M10) a rock
// chip (deposit hits, debris, the Gallery's ejected casing). Mostly white or cream so the renderer can tint
// them (a multiply tint: the chip's shade facet stays darker than its lit face); alpha falls off softly.
import { fillP } from './passes.ts';
import { circle, poly, roundPoly, rrect, starPts } from './shapes.ts';
import type { FxSpec, Generator } from './types.ts';

export const fxGen: Generator<FxSpec> = (c, spec, px, env) => {
  const { pal } = env;
  const W = spec.w * 100;
  const H = spec.h * 100;
  const cx = W / 2;
  const cy = H / 2;
  c.save();
  c.scale(px / 100, px / 100);
  switch (spec.fx) {
    case 'spark':
      fillP(
        c,
        poly([
          [cx, 1],
          [cx + 3, cy],
          [cx, H - 1],
          [cx - 3, cy],
        ]),
        pal.white,
      );
      break;
    case 'dot': {
      const g = c.createRadialGradient(cx, cy, 0, cx, cy, W / 2);
      g.addColorStop(0, 'rgba(255,255,255,1)');
      g.addColorStop(0.35, 'rgba(255,255,255,0.75)');
      g.addColorStop(1, 'rgba(255,255,255,0)');
      c.fillStyle = g;
      c.fillRect(0, 0, W, H);
      break;
    }
    case 'star4':
      fillP(c, roundPoly(starPts(cx, cy, W / 2 - 1, W * 0.12, 4, -90), 0.8), pal.white);
      break;
    case 'coin': {
      const m = pal.sw('ui.money');
      fillP(c, circle(cx, cy, W / 2 - 1), m.outline);
      fillP(c, circle(cx, cy, W / 2 - 3), m.base);
      fillP(c, rrect(cx - 1.4, cy - W * 0.24, 2.8, W * 0.48, 1.4), m.shade);
      fillP(c, circle(cx - W * 0.14, cy - W * 0.14, W * 0.07), m.hi);
      break;
    }
    case 'smoke': {
      const g = c.createRadialGradient(cx, cy, 0, cx, cy, W / 2);
      g.addColorStop(0, pal.rgba('ui.cream2', 0.9));
      g.addColorStop(0.6, pal.rgba('ui.cream2', 0.45));
      g.addColorStop(1, pal.rgba('ui.cream2', 0));
      c.fillStyle = g;
      c.fillRect(0, 0, W, H);
      break;
    }
    case 'ring':
      c.beginPath();
      c.arc(cx, cy, W / 2 - 4, 0, Math.PI * 2);
      c.strokeStyle = 'rgba(255,255,255,0.9)';
      c.lineWidth = 3.5;
      c.stroke();
      c.beginPath();
      c.arc(cx, cy, W / 2 - 8, 0, Math.PI * 2);
      c.strokeStyle = 'rgba(255,255,255,0.35)';
      c.lineWidth = 4;
      c.stroke();
      break;
    case 'chip': {
      // an irregular pebble: light face + shade facet, outlined (tinted by material at runtime)
      const pts = [
        [W * 0.18, H * 0.34],
        [W * 0.48, H * 0.12],
        [W * 0.84, H * 0.3],
        [W * 0.88, H * 0.66],
        [W * 0.56, H * 0.9],
        [W * 0.16, H * 0.72],
      ] as const;
      fillP(c, roundPoly(pts, 1.2), pal.hex('ui.ink'));
      const inner = pts.map(([x, y]) => [cx + (x - cx) * 0.78, cy + (y - cy) * 0.78] as const);
      fillP(c, roundPoly(inner, 1), pal.hex('ui.cream'));
      // the shade facet: the centre + the lower-left rim
      fillP(c, poly([[cx, cy], ...inner.slice(3)]), pal.hex('ui.cream2'));
      break;
    }
    default: {
      const tok = `confetti.${(spec.index % 3) + 1}`;
      fillP(c, rrect(W * 0.2, H * 0.3, W * 0.6, H * 0.4, 1.2), pal.hex(tok));
    }
  }
  c.restore();
};
